import { CenterLinePoint, ThermoDetail, ThermoStats, ThermoStatsWithCount } from "../types";
import {
  EMISSIVITY_LOWER,
  EMISSIVITY_REFERENCE,
  EMISSIVITY_UPPER,
  correctTemperatureForEmissivity
} from "./thermoEmissivity";
import {
  ThermoHeightCalibration,
  aggregateCenterLinesByProjectedHeight
} from "./thermoHeightProjection";

const EMPTY_STATS: ThermoStats = {
  minTemperature: 0,
  meanTemperature: 0,
  maxTemperature: 0,
  minViscosity: 0,
  meanViscosity: 0,
  maxViscosity: 0
};

export function averageCenterLines(details: ThermoDetail[]): CenterLinePoint[] {
  if (!details.length) return [];
  const buckets = new Map<
    number,
    {
      tempSum: number;
      temp0875Sum: number;
      tempUpperSum: number;
      tempLowerSum: number;
      viscSum: number;
      count: number;
    }
  >();
  details.forEach((detail) => {
    detail.centerLine.forEach((point) => {
      const bucket = buckets.get(point.y) || {
        tempSum: 0,
        temp0875Sum: 0,
        tempUpperSum: 0,
        tempLowerSum: 0,
        viscSum: 0,
        count: 0
      };
      bucket.tempSum += point.temperature;
      bucket.temp0875Sum +=
        point.temperature_epsilon_0875 ??
        correctTemperatureForEmissivity(point.temperature, detail.epsilon, EMISSIVITY_REFERENCE);
      bucket.tempUpperSum +=
        point.temperature_upper ??
        correctTemperatureForEmissivity(point.temperature, detail.epsilon, EMISSIVITY_UPPER);
      bucket.tempLowerSum +=
        point.temperature_lower ??
        correctTemperatureForEmissivity(point.temperature, detail.epsilon, EMISSIVITY_LOWER);
      bucket.viscSum += point.viscosity;
      bucket.count += 1;
      buckets.set(point.y, bucket);
    });
  });

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([y, bucket]) => ({
      y,
      temperature: bucket.tempSum / bucket.count,
      temperature_epsilon_0875: bucket.temp0875Sum / bucket.count,
      temperature_upper: bucket.tempUpperSum / bucket.count,
      temperature_lower: bucket.tempLowerSum / bucket.count,
      viscosity: bucket.viscSum / bucket.count
    }));
}

export function summarizeDetails(
  details: ThermoDetail[],
  heightCalibration?: ThermoHeightCalibration | null
) {
  const averaged =
    heightCalibration && details.length
      ? aggregateCenterLinesByProjectedHeight(details, heightCalibration) || averageCenterLines(details)
      : averageCenterLines(details);
  const stats = computeStatsFromCenterLine(averaged);
  const bottleStats = computeAggregatedBottleStats(details);
  return { centerLine: averaged, stats, bottleStats };
}

function computeStatsFromCenterLine(centerLine: CenterLinePoint[]) {
  if (!centerLine.length) {
    return EMPTY_STATS;
  }
  let minT = Number.POSITIVE_INFINITY;
  let maxT = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;
  let sumT = 0;
  let sumV = 0;
  centerLine.forEach((p) => {
    minT = Math.min(minT, p.temperature);
    maxT = Math.max(maxT, p.temperature);
    minV = Math.min(minV, p.viscosity);
    maxV = Math.max(maxV, p.viscosity);
    sumT += p.temperature;
    sumV += p.viscosity;
  });
  const count = centerLine.length;
  return {
    minTemperature: minT,
    meanTemperature: sumT / count,
    maxTemperature: maxT,
    minViscosity: minV,
    meanViscosity: sumV / count,
    maxViscosity: maxV
  };
}

function computeAggregatedBottleStats(details: ThermoDetail[]): ThermoStats {
  const bottleStats = details
    .map((detail) => detail.bottleStats)
    .filter(Boolean) as ThermoStatsWithCount[];

  if (!bottleStats.length) {
    return EMPTY_STATS;
  }

  let minT = Number.POSITIVE_INFINITY;
  let maxT = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;
  let sumT = 0;
  let sumV = 0;
  let totalCount = 0;

  bottleStats.forEach((stat) => {
    const count = Number.isFinite(stat.count) ? stat.count : 0;
    if (count <= 0) return;
    minT = Math.min(minT, stat.minTemperature);
    maxT = Math.max(maxT, stat.maxTemperature);
    minV = Math.min(minV, stat.minViscosity);
    maxV = Math.max(maxV, stat.maxViscosity);
    sumT += stat.meanTemperature * count;
    sumV += stat.meanViscosity * count;
    totalCount += count;
  });

  if (!totalCount || !Number.isFinite(minT) || !Number.isFinite(minV)) {
    return EMPTY_STATS;
  }

  return {
    minTemperature: minT,
    meanTemperature: sumT / totalCount,
    maxTemperature: maxT,
    minViscosity: minV,
    meanViscosity: sumV / totalCount,
    maxViscosity: maxV
  };
}
